import React, { useEffect, useState } from "react"
import { useSearch } from "@tanstack/react-router"
import { Terminal } from "lucide-react"

import { Button } from "@aix/ui"

import { MfaCheckError, MfaGate } from "../components/auth/MfaGate"
import { useAuth } from "../lib/auth-context"
import { hasStrongSession } from "../lib/mfaGatePolicy"
import { supabase } from "../lib/supabase"

const PORT_STORAGE_KEY = "cliAuthPort"

/**
 * CLI authorization page. Opened by `aix-token login` as
 * `https://agent.aixlabs.co/cli-auth?port=<loopbackPort>`.
 *
 * Flow: sign in (Google) → gate ensures AAL2 or a passkey → hand the session back to
 * the CLI's loopback server via a top-level redirect to http://127.0.0.1:<port>.
 * The port is stashed in sessionStorage so it survives the OAuth round-trip.
 * Public route (not ProtectedRoute) — it manages its own auth + gate.
 */
export default function CliAuthScreen() {
  const search = useSearch({ from: "/cli-auth" }) as { port?: string }
  const { user, loading, signInWithGoogle, mfaStatus } = useAuth()
  const [port, setPort] = useState<string | null>(null)
  const [error, setError] = useState("")
  const [signingIn, setSigningIn] = useState(false)
  const [redirecting, setRedirecting] = useState(false)

  // Capture + persist the loopback port (survives the OAuth redirect round-trip).
  useEffect(() => {
    const p = search.port || sessionStorage.getItem(PORT_STORAGE_KEY)
    if (p) {
      sessionStorage.setItem(PORT_STORAGE_KEY, String(p))
      setPort(String(p))
    }
  }, [search.port])

  // Once strongly authenticated, hand the session to the loopback server.
  useEffect(() => {
    if (!user || !port || redirecting) return
    if (mfaStatus !== "ok") return // wait for the MFA gate to clear

    let cancelled = false
    async function handoff() {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession()
        if (!session) {
          setError("No active session — try again.")
          return
        }
        if (!hasStrongSession(session.access_token)) {
          // Exempt app accounts still need strong authentication for token issuance.
          setError("Your session isn't MFA-verified. Sign out and sign back in to continue.")
          return
        }
        const url = new URL(`http://127.0.0.1:${port}/callback`)
        url.searchParams.set("access_token", session.access_token)
        url.searchParams.set("refresh_token", session.refresh_token)
        url.searchParams.set("email", session.user.email ?? "")
        url.searchParams.set("expires_at", String(session.expires_at ?? ""))
        sessionStorage.removeItem(PORT_STORAGE_KEY)
        if (cancelled) return
        setRedirecting(true)
        // Top-level navigation to localhost is allowed from HTTPS (no mixed-content block).
        window.location.href = url.toString()
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to complete CLI authorization")
      }
    }
    void handoff()
    return () => {
      cancelled = true
    }
  }, [user, port, mfaStatus, redirecting])

  async function handleSignIn() {
    setSigningIn(true)
    setError("")
    // Return to /cli-auth after the OAuth callback (port restored from sessionStorage).
    localStorage.setItem("authRedirect", "/cli-auth")
    const result = await signInWithGoogle()
    if (result.error) {
      setError(result.error)
      setSigningIn(false)
    }
  }

  const card = (body: React.ReactNode) => (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-6 rounded-lg border border-border bg-card p-8 text-center">
        <Terminal className="mx-auto h-10 w-10 text-primary" />
        <h1 className="text-xl font-bold text-foreground">Authorize CLI access</h1>
        {body}
      </div>
    </div>
  )

  if (!port) {
    return card(
      <p className="text-sm text-muted-foreground">
        Missing the loopback port. Start this from your terminal with <code>aix-token login</code>.
      </p>,
    )
  }

  if (loading) {
    return card(<div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />)
  }

  // Signed in but needs to enroll / step up MFA → reuse the shared gate.
  if (user && (mfaStatus === "enroll" || mfaStatus === "challenge")) {
    return <MfaGate />
  }

  if (user && mfaStatus === "error") {
    return <MfaCheckError />
  }

  if (user) {
    return card(
      <>
        <p className="text-sm text-muted-foreground">
          {redirecting ? "Authorized — returning to your terminal…" : "Completing authorization…"}
        </p>
        {error && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
      </>,
    )
  }

  return card(
    <>
      <p className="text-sm text-muted-foreground">
        Sign in to grant your terminal a short-lived, MFA-verified session for minting prod access tokens.
      </p>
      {error && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
      <Button className="w-full" loading={signingIn} onClick={() => void handleSignIn()}>
        Sign in with Google
      </Button>
    </>,
  )
}
